import { useMemo, type ReactNode } from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { globalToastRef } from "./globalToastRef";
import { handleMutationSuccess } from "./mutationTracking";
import { ONBOARDING_INCOMPLETE_ERROR } from "../hooks/requests/_helpers";

interface QueryClientProviderWithGlobalErrorsProps {
  children: ReactNode;
}

export const QueryClientProviderWithGlobalErrors = ({
  children,
}: QueryClientProviderWithGlobalErrorsProps) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 30,
            gcTime: 1000 * 60 * 30,
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            const message = error?.message ?? String(error);
            if (message === ONBOARDING_INCOMPLETE_ERROR) return;
            // Queries que prefieren manejar errores localmente (detail
            // pages que pueden ser borradas por su propia mutation) marcan
            // `meta.skipGlobalErrorToast: true` para evitar toasts fantasma.
            if (query.meta?.skipGlobalErrorToast === true) return;
            globalToastRef.current?.(message, "error");
          },
        }),
        mutationCache: new MutationCache({
          onSuccess: handleMutationSuccess,
        }),
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  );
};
